import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi, normalizeList } from '../../../lib/admin-api/index.js';
import { useFilterPresets } from '../../../shared/filter-presets/use-filter-presets.js';
import {
  ADMIN_NOTIFICATION_TYPES,
  useAdminNotificationRefresh,
} from '../../../shared/realtime/admin-notification-events.js';
import {
  DEFAULT_DOCUMENT_SORT,
  DOCUMENT_SORT_FIELDS,
  buildDocumentsRequestDto,
  mapDocumentsResponseToDto,
} from '../dto/documents-list.dto.js';
import { buildDocumentsTableModel } from '../model/documents-list.model.js';

const STATUS_FILTER_OPTIONS = ['new', 'approved', 'rejected', 'pending'];
const TYPE_FILTER_OPTIONS = ['id-front', 'id-back', 'poa', 'other'];
const DOCUMENT_PAGE_SIZE = 20;
const DOCUMENT_FILTER_STORAGE_KEY = 'admin_documents_list_filters';
const DEFAULT_SORT_ORDER_BY_FIELD = Object.freeze({
  created: 'desc',
  modified: 'desc',
  expirationDate: 'desc',
  name: 'asc',
  status: 'asc',
  type: 'asc',
});

const EMPTY_DOCUMENT_FILTERS = Object.freeze({
  name: '',
  type: '',
  status: '',
  translationRequired: false,
  assignedOnly: false,
  countriesOnly: [],
});

function readSearchParams() {
  if (typeof window === 'undefined') {
    return new URLSearchParams();
  }

  return new URLSearchParams(window.location.search);
}

function readStringParam(params, key) {
  return String(params.get(key) || '').trim();
}

function readBooleanParam(params, key) {
  const value = String(params.get(key) || '').trim().toLowerCase();
  return value === '1' || value === 'true';
}

function getInitialFiltersFromUrl() {
  const params = readSearchParams();

  return {
    name: readStringParam(params, 'name'),
    type: readStringParam(params, 'type'),
    status: readStringParam(params, 'status'),
    translationRequired: readBooleanParam(params, 'translationRequired'),
    assignedOnly: readBooleanParam(params, 'assignedOnly'),
    countriesOnly: params.getAll('country').map((item) => String(item || '').trim().toUpperCase()).filter(Boolean),
  };
}

function normalizeDocumentFilters(filters) {
  return {
    ...EMPTY_DOCUMENT_FILTERS,
    ...filters,
    name: String(filters?.name || '').trim(),
    type: String(filters?.type || '').trim(),
    status: String(filters?.status || '').trim(),
    translationRequired: filters?.translationRequired === true,
    assignedOnly: filters?.assignedOnly === true,
    countriesOnly: Array.isArray(filters?.countriesOnly)
      ? filters.countriesOnly.map((item) => String(item || '').trim().toUpperCase()).filter(Boolean)
      : [],
  };
}

function readStoredFilters() {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(DOCUMENT_FILTER_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function hasActiveFilters(filters) {
  const normalized = normalizeDocumentFilters(filters);

  return Boolean(
    normalized.name
      || normalized.type
      || normalized.status
      || normalized.translationRequired
      || normalized.assignedOnly
      || normalized.countriesOnly.length > 0,
  );
}

function getInitialDocumentFilters() {
  const urlFilters = normalizeDocumentFilters(getInitialFiltersFromUrl());
  if (hasActiveFilters(urlFilters)) {
    return urlFilters;
  }

  return normalizeDocumentFilters(readStoredFilters() || EMPTY_DOCUMENT_FILTERS);
}

function getInitialDocumentId() {
  return readStringParam(readSearchParams(), 'document');
}

function buildFilterSearchParams(filters) {
  const normalized = normalizeDocumentFilters(filters);
  const params = new URLSearchParams();

  if (normalized.name) {
    params.set('name', normalized.name);
  }
  if (normalized.type) {
    params.set('type', normalized.type);
  }
  if (normalized.status) {
    params.set('status', normalized.status);
  }
  normalized.countriesOnly.forEach((country) => {
    params.append('country', country);
  });
  if (normalized.translationRequired) {
    params.set('translationRequired', '1');
  }
  if (normalized.assignedOnly) {
    params.set('assignedOnly', '1');
  }

  return params;
}

function persistDocumentFilters(filters) {
  if (typeof window === 'undefined') {
    return;
  }

  const normalized = normalizeDocumentFilters(filters);
  const active = hasActiveFilters(normalized);

  try {
    if (active) {
      window.sessionStorage.setItem(DOCUMENT_FILTER_STORAGE_KEY, JSON.stringify(normalized));
    } else {
      window.sessionStorage.removeItem(DOCUMENT_FILTER_STORAGE_KEY);
    }
  } catch {
    // ignore unavailable storage
  }

  const params = buildFilterSearchParams(normalized);
  const nextSearch = params.toString();
  const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash || ''}`;
  window.history.replaceState(window.history.state, '', nextUrl);
}

function mapCountryOptions(payload) {
  const raw = payload?.countries ?? payload?.items ?? payload?.data ?? payload;
  const list = Array.isArray(raw) ? raw : (raw && typeof raw === 'object' ? Object.values(raw) : []);

  const options = list
    .map((item) => {
      if (typeof item === 'string') {
        const value = item.trim().toUpperCase();
        return value ? { value, label: value } : null;
      }

      if (!item || typeof item !== 'object') {
        return null;
      }

      const value = String(item.code || item.value || item.country || '').trim().toUpperCase();
      if (!value) {
        return null;
      }

      const name = String(item.name || item.label || '').trim();
      return {
        value,
        label: name ? `${value} - ${name}` : value,
      };
    })
    .filter(Boolean);

  const uniq = new Map();
  options.forEach((option) => uniq.set(option.value, option));
  return Array.from(uniq.values()).sort((a, b) => a.value.localeCompare(b.value));
}

function formatDateInput(value) {
  const raw = String(value || '').trim();
  if (!raw) {
    return '';
  }

  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toISOString().slice(0, 10);
}

function pickFirst(...values) {
  return values.find((value) => value !== undefined && value !== null && String(value).trim() !== '');
}

function mapAdminProfileOptions(payload) {
  return normalizeList(payload)
    .map((profile, index) => {
      const id = String(pickFirst(profile?.uid, profile?.id) || '').trim();
      if (!id) {
        return null;
      }

      const firstName = pickFirst(profile?.firstName, profile?.first_name);
      const lastName = pickFirst(profile?.lastName, profile?.last_name);
      const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
      const fallbackName = pickFirst(profile?.name, profile?.full_name, profile?.username, profile?.email, `Profile ${index + 1}`);
      const label = fullName || String(fallbackName || '').trim() || id;

      return { id, label };
    })
    .filter(Boolean)
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function useDocumentsList() {
  const { token } = useAuth();
  const initialFilters = useMemo(() => getInitialDocumentFilters(), []);
  const [rows, setRows] = useState([]);
  const [hasMoreDocuments, setHasMoreDocuments] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(() => hasActiveFilters(initialFilters));
  const [filters, setFilters] = useState(initialFilters);
  const [sort, setSort] = useState(DEFAULT_DOCUMENT_SORT);
  const [countryOptions, setCountryOptions] = useState([]);
  const [selectedDocumentId, setSelectedDocumentId] = useState(() => getInitialDocumentId());
  const [documentViewer, setDocumentViewer] = useState({
    isLoading: false,
    error: '',
    url: '',
    mime: '',
  });
  const [documentForm, setDocumentForm] = useState({
    translation: '',
    expirationDate: '',
    status: '',
    translationRequired: false,
  });
  const [isDocumentSaving, setIsDocumentSaving] = useState(false);
  const [documentSaveError, setDocumentSaveError] = useState('');
  const [documentSaveSuccess, setDocumentSaveSuccess] = useState('');
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [assignTargetDocumentId, setAssignTargetDocumentId] = useState('');
  const [assignTargetProfileName, setAssignTargetProfileName] = useState('');
  const [adminProfileOptions, setAdminProfileOptions] = useState([]);
  const [isAdminProfilesLoading, setIsAdminProfilesLoading] = useState(false);
  const [selectedManagerId, setSelectedManagerId] = useState('');
  const [isAssignmentSaving, setIsAssignmentSaving] = useState(false);
  const [assignmentError, setAssignmentError] = useState('');

  const applyPresetFilters = useCallback((presetFilters) => {
    setFilters(normalizeDocumentFilters(presetFilters));
    setIsFilterOpen(true);
  }, []);

  const filterPresetState = useFilterPresets({
    storageKey: 'admin_document_filter_presets',
    filters,
    onApply: applyPresetFilters,
  });

  useEffect(() => {
    persistDocumentFilters(filters);
  }, [filters]);

  const loadDocuments = useCallback(async ({ offset = 0, append = false } = {}) => {
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }
    setError('');

    try {
      const payload = buildDocumentsRequestDto(filters, {
        limit: DOCUMENT_PAGE_SIZE,
        offset,
      }, sort);
      const response = await adminApi.getDocuments(token, payload);
      const dto = mapDocumentsResponseToDto(response);
      const nextRows = buildDocumentsTableModel(dto);
      setRows((previousRows) => (append ? [...previousRows, ...nextRows] : nextRows));
      setHasMoreDocuments(nextRows.length === DOCUMENT_PAGE_SIZE);
    } catch (requestError) {
      if (!append) {
        setRows([]);
        setHasMoreDocuments(false);
      }
      setError(requestError.message || 'Failed to load documents');
    } finally {
      if (append) {
        setIsLoadingMore(false);
      } else {
        setIsLoading(false);
      }
    }
  }, [token, filters, sort]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  useAdminNotificationRefresh(ADMIN_NOTIFICATION_TYPES.documentUploaded, () => {
    loadDocuments({ offset: 0, append: false });
  });

  const loadMoreDocuments = useCallback(() => {
    if (isLoading || isLoadingMore || !hasMoreDocuments) {
      return;
    }

    loadDocuments({ offset: rows.length, append: true });
  }, [hasMoreDocuments, isLoading, isLoadingMore, loadDocuments, rows.length]);

  useEffect(() => {
    let isCancelled = false;

    const loadCountries = async () => {
      try {
        const response = await adminApi.getCountryDictionary(token);
        if (!isCancelled) {
          setCountryOptions(mapCountryOptions(response));
        }
      } catch {
        if (!isCancelled) {
          setCountryOptions([]);
        }
      }
    };

    loadCountries();

    return () => {
      isCancelled = true;
    };
  }, [token]);

  const statusOptions = STATUS_FILTER_OPTIONS;
  const typeOptions = TYPE_FILTER_OPTIONS;
  const selectedDocument = useMemo(
    () => rows.find((row) => row.key === selectedDocumentId)?.source || null,
    [rows, selectedDocumentId],
  );

  useEffect(() => {
    if (!selectedDocument) {
      setDocumentForm({
        translation: '',
        expirationDate: '',
        status: '',
        translationRequired: false,
      });
      setDocumentSaveError('');
      setDocumentSaveSuccess('');
      return;
    }

    const translationRequired = selectedDocument.translationRequired === true
      || String(selectedDocument.translationRequired || '').trim().toLowerCase() === 'true';

    setDocumentForm({
      translation: String(selectedDocument.translation || '').trim(),
      expirationDate: formatDateInput(selectedDocument.expiration),
      status: String(selectedDocument.status || '').trim(),
      translationRequired,
    });
    setDocumentSaveError('');
    setDocumentSaveSuccess('');
  }, [selectedDocument]);

  useEffect(() => {
    let isCancelled = false;
    let objectUrl = '';

    if (!selectedDocumentId) {
      setDocumentViewer({
        isLoading: false,
        error: '',
        url: '',
        mime: '',
      });
      return undefined;
    }

    setDocumentViewer({
      isLoading: true,
      error: '',
      url: '',
      mime: '',
    });

    const loadDocument = async () => {
      try {
        const response = await fetch(
          adminApi.client.resolveUrl(`/admin/document/client/download/${encodeURIComponent(selectedDocumentId)}`),
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (!response.ok) {
          throw new Error(`Failed to load document (status ${response.status})`);
        }

        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);

        if (!isCancelled) {
          setDocumentViewer({
            isLoading: false,
            error: '',
            url: objectUrl,
            mime: blob.type || '',
          });
        }
      } catch (requestError) {
        if (!isCancelled) {
          setDocumentViewer({
            isLoading: false,
            error: requestError.message || 'Failed to load document',
            url: '',
            mime: '',
          });
        }
      }
    };

    loadDocument();

    return () => {
      isCancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [selectedDocumentId, token]);

  const saveSelectedDocument = async () => {
    if (!selectedDocumentId) {
      return;
    }

    setIsDocumentSaving(true);
    setDocumentSaveError('');
    setDocumentSaveSuccess('');

    try {
      const payload = {
        translation: String(documentForm.translation || '').trim() || null,
        translationRequired: documentForm.translationRequired ? true : null,
        expirationDate: documentForm.expirationDate
          ? new Date(`${documentForm.expirationDate}T00:00:00.000Z`).toISOString()
          : null,
        status: String(documentForm.status || '').trim() || null,
      };

      const response = await adminApi.requestWithAuth({
        token,
        method: 'PATCH',
        path: `/admin/document/client/${encodeURIComponent(selectedDocumentId)}`,
        body: payload,
      });

      const dto = mapDocumentsResponseToDto(response);
      if (dto.length > 0) {
        setRows(buildDocumentsTableModel(dto));
      } else {
        setRows((prev) => prev.map((row) => (
          row.key === selectedDocumentId
            ? {
              ...row,
              status: payload.status || '-',
              translation: payload.translation || '-',
              translationRequired: payload.translationRequired,
              expirationAt: formatDateInput(payload.expirationDate),
              source: {
                ...row.source,
                status: payload.status,
                translation: payload.translation,
                translationRequired: payload.translationRequired,
                expiration: payload.expirationDate,
              },
            }
            : row
        )));
      }

      setDocumentSaveSuccess('Document updated');
      setSelectedDocumentId('');
    } catch (requestError) {
      setDocumentSaveError(requestError.message || 'Failed to update document');
    } finally {
      setIsDocumentSaving(false);
    }
  };

  const updateFilter = (event) => {
    const { name, value, type, checked } = event.target;
    setFilters((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const resetAndCloseFilters = () => {
    setFilters(normalizeDocumentFilters(EMPTY_DOCUMENT_FILTERS));
    setIsFilterOpen(false);
  };

  const changeSort = useCallback((field) => {
    const normalizedField = String(field || '').trim();
    if (!DOCUMENT_SORT_FIELDS.includes(normalizedField)) {
      return;
    }

    setSort((currentSort) => {
      if (currentSort.sortBy === normalizedField) {
        return {
          sortBy: normalizedField,
          sortOrder: currentSort.sortOrder === 'asc' ? 'desc' : 'asc',
        };
      }

      return {
        sortBy: normalizedField,
        sortOrder: DEFAULT_SORT_ORDER_BY_FIELD[normalizedField] || 'asc',
      };
    });
  }, []);

  const loadAdminProfiles = useCallback(async () => {
    setIsAdminProfilesLoading(true);
    setAssignmentError('');

    try {
      const response = await adminApi.getProfiles(token, { isAdmin: true, limit: 100 });
      setAdminProfileOptions(mapAdminProfileOptions(response));
    } catch (requestError) {
      setAdminProfileOptions([]);
      setAssignmentError(requestError.message || 'Failed to load admin profiles');
    } finally {
      setIsAdminProfilesLoading(false);
    }
  }, [token]);

  const openAssignModal = async (row) => {
    const documentId = String(row?.key || '').trim();
    if (!documentId) {
      return;
    }

    setAssignTargetDocumentId(documentId);
    setAssignTargetProfileName(String(row?.profileName || '').trim() || '-');
    setSelectedManagerId(String(row?.assignedToId || '').trim());
    setAssignmentError('');
    setIsAssignModalOpen(true);
    await loadAdminProfiles();
  };

  const closeAssignModal = (force = false) => {
    if (isAssignmentSaving && !force) {
      return;
    }
    setIsAssignModalOpen(false);
    setAssignTargetDocumentId('');
    setAssignTargetProfileName('');
    setSelectedManagerId('');
    setAssignmentError('');
  };

  const assignManager = async () => {
    if (!assignTargetDocumentId) {
      setAssignmentError('Document id is required');
      return;
    }

    const managerId = String(selectedManagerId || '').trim();
    if (!managerId) {
      setAssignmentError('Select a profile to assign');
      return;
    }

    setIsAssignmentSaving(true);
    setAssignmentError('');

    try {
      await adminApi.assignDocument(token, assignTargetDocumentId, managerId);
      closeAssignModal(true);
      await loadDocuments();
    } catch (requestError) {
      setAssignmentError(requestError.message || 'Failed to assign manager');
    } finally {
      setIsAssignmentSaving(false);
    }
  };

  const unassignManager = async () => {
    if (!assignTargetDocumentId) {
      setAssignmentError('Document id is required');
      return;
    }

    setIsAssignmentSaving(true);
    setAssignmentError('');

    try {
      await adminApi.unassignDocument(token, assignTargetDocumentId);
      closeAssignModal(true);
      await loadDocuments();
    } catch (requestError) {
      setAssignmentError(requestError.message || 'Failed to unassign manager');
    } finally {
      setIsAssignmentSaving(false);
    }
  };

  return {
    rows,
    filters,
    sort,
    statusOptions,
    typeOptions,
    countryOptions,
    isFilterOpen,
    isLoading,
    isLoadingMore,
    hasMoreDocuments,
    error,
    selectedDocument,
    documentViewer,
    documentForm,
    isDocumentSaving,
    documentSaveError,
    documentSaveSuccess,
    isAssignModalOpen,
    assignTargetProfileName,
    adminProfileOptions,
    isAdminProfilesLoading,
    selectedManagerId,
    isAssignmentSaving,
    assignmentError,
    changeSort,
    updateFilter,
    onCountriesFilterChange: (countries) => setFilters((prev) => ({
      ...prev,
      countriesOnly: Array.isArray(countries)
        ? countries.map((item) => String(item || '').trim().toUpperCase()).filter(Boolean)
        : [],
    })),
    resetAndCloseFilters,
    setIsFilterOpen,
    openAssignModal,
    closeAssignModal,
    onSelectManager: (managerId) => setSelectedManagerId(String(managerId || '').trim()),
    assignManager,
    unassignManager,
    openDetails: (documentId) => setSelectedDocumentId(String(documentId || '')),
    closeDetails: () => setSelectedDocumentId(''),
    onDocumentFormChange: (partial) => setDocumentForm((prev) => ({ ...prev, ...partial })),
    saveSelectedDocument,
    loadMoreDocuments,
    ...filterPresetState,
  };
}
